"use client";

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import Link from 'next/link';
import { resolveImg } from '../../../lib/config';

interface NewsItem {
  id: number;
  title: string;
  summary: string | null;
  thumbnail: string | null;
  createdAt: string;
}

const PAGE_SIZE = 5;

export default function NewsPage() {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    setLoading(true);
    const lang = locale === 'zh' ? 'ZH' : 'EN';
    fetch(`/api/news/public?page=${page}&limit=${PAGE_SIZE}&language=${lang}`)
      .then(res => res.json())
      .then(response => {
        setNewsList(response.data || []);
        setTotal(response.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, locale]);

  return (
    <div className="container" style={{ padding: '160px 20px 60px', minHeight: '60vh', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ color: 'var(--un-blue-dark)', marginBottom: '40px', textAlign: 'center' }}>
        {isZh ? '新闻与动态' : 'News & Updates'}
      </h1>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>{isZh ? '加载中...' : 'Loading...'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          {newsList.map(n => {
            const title = n.title;
            const summary = n.summary || '';
            
            return (
              <Link href={`/${locale}/news/${n.id}`} key={n.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap', backgroundColor: '#fff', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', transition: 'all 0.3s', cursor: 'pointer', border: '1px solid transparent' }}
                     onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'var(--un-blue)'; }}
                     onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}>
                  
                  {n.thumbnail && (
                    <div style={{ flex: '1 1 200px', minWidth: '250px', height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                      <img src={resolveImg(n.thumbnail)} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}

                  <div style={{ flex: '3 1 250px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <h3 style={{ margin: '0 0 8px 0', color: 'var(--un-blue-dark)', fontSize: '1.3rem' }}>{title}</h3>
                    <span style={{ fontSize: '0.85rem', color: 'var(--un-gold)', fontWeight: 600, marginBottom: '8px' }}>
                      {new Date(n.createdAt).toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                    <p style={{ margin: 0, color: '#666', lineHeight: '1.6', fontSize: '0.95rem' }}>{summary}</p>
                    <span style={{ marginTop: '10px', color: 'var(--un-blue)', fontSize: '0.9rem', fontWeight: 500 }}>
                      {isZh ? '阅读更多 →' : 'Read more →'}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
          
          {newsList.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              {isZh ? '抱歉，暂无新闻动态发布' : 'No news available at the moment.'}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '40px', alignItems: 'center' }}>
          <button 
            onClick={() => setPage(p => Math.max(1, p - 1))} 
            disabled={page === 1}
            style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '6px', cursor: page === 1 ? 'not-allowed' : 'pointer', background: page === 1 ? '#f5f5f5' : '#fff', color: page === 1 ? '#ccc' : '#333' }}
          >
            {isZh ? '上一页' : 'Prev'}
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              style={{ padding: '8px 14px', border: p === page ? '2px solid var(--un-blue)' : '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: p === page ? 'var(--un-blue)' : '#fff', color: p === page ? '#fff' : '#333', fontWeight: p === page ? 700 : 400 }}
            >
              {p}
            </button>
          ))}
          <button 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
            disabled={page === totalPages}
            style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '6px', cursor: page === totalPages ? 'not-allowed' : 'pointer', background: page === totalPages ? '#f5f5f5' : '#fff', color: page === totalPages ? '#ccc' : '#333' }}
          >
            {isZh ? '下一页' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
}
